import { LoadingButton } from "@mui/lab";
import { Card, CardContent, Grid, Box, Chip, Paper } from "@mui/material";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { FormProvider, RHFFileField } from "../../../common/hook-form";
import UploadIcon from "@mui/icons-material/Upload";
import { apiConstants, postGeneric } from "../../../common/constants";
import { useSnackbar } from "notistack";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Typography from "@mui/material/Typography";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ImageIcon from "@mui/icons-material/Image";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";

function UploadImage(props) {
  const { id, setReload, setOpen } = props;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const methods = useForm();
  const { handleSubmit, reset } = methods;
  const { enqueueSnackbar } = useSnackbar();

  const onSubmit = (data) => {
    setIsSubmitting(true);
    let formData = new FormData();
    if (data.file1) {
      formData.append("file1", data.file1[0]);
    }
    if (data.file2) {
      formData.append("file2", data.file2[0]);
    }
    if (data.file3) {
      formData.append("file3", data.file3[0]);
    }
    if (data.file4) {
      formData.append("file4", data.file4[0]);
    }
    if (data.file5) {
      formData.append("file5", data.file5[0]);
    }
    if (data.file6) {
      formData.append("file6", data.file6[0]);
    }
    postGeneric(
      `${apiConstants.IMAGE_UPLOAD_WEIGHT_DISCREPANCY}/${id}/MERCHANT`,
      formData
    )
      .then((res) => {
        if (res.status === 200) {
          enqueueSnackbar("Image is uploaded Successfully", {
            variant: "success",
          });
          setReload((reload) => reload + 1);
          setOpen((open) => false);
          reset();
        } else {
          enqueueSnackbar("Image is not uploaded", { variant: "error" });
          setReload((reload) => reload + 1);
          setOpen((open) => false);
          reset();
        }
      })
      .catch((err) => {
        console.log(err.response);
        enqueueSnackbar(err.response?.data || "Image is not uploaded", { variant: "error" });
      }).finally(() => {
        setIsSubmitting(false);
      });
  };
  return (
    <Card elevation={2} sx={{ borderRadius: 1 }}>
      <CardContent sx={{ p: 2 }}>
        <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
          <Box sx={{ mb: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <CloudUploadIcon sx={{ mr: 0.5, color: 'primary.main', fontSize: '1.2rem' }} />
              <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 600 }}>
                Upload Images
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary">
              Upload up to 6 images for weight discrepancy documentation
            </Typography>
          </Box>

          <Grid container spacing={1}>
            {[1, 2, 3, 4, 5, 6].map((imageNumber) => (
              <Grid item xs={12} key={imageNumber}>
                <Accordion 
                  sx={{ 
                    border: '1px solid', 
                    borderColor: 'divider',
                    borderRadius: 1,
                    '&:before': { display: 'none' },
                    '&.Mui-expanded': {
                      margin: 0,
                      '&:first-of-type': {
                        marginTop: 0,
                      },
                    }
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ fontSize: '1.2rem' }} />}
                    aria-controls={`panel${imageNumber}-content`}
                    id={`panel${imageNumber}-header`}
                    sx={{
                      backgroundColor: 'grey.50',
                      borderRadius: '4px 4px 0 0',
                      minHeight: '40px',
                      '&.Mui-expanded': {
                        borderRadius: '4px 4px 0 0',
                        minHeight: '40px',
                      },
                      '& .MuiAccordionSummary-content': {
                        margin: '8px 0',
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      <ImageIcon sx={{ mr: 1, color: 'primary.main', fontSize: '1.1rem' }} />
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Image {imageNumber}
                      </Typography>
                      <Chip 
                        label="Optional" 
                        size="small" 
                        color="default" 
                        variant="outlined"
                        sx={{ ml: 'auto', fontSize: '0.7rem', height: '20px' }}
                      />
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 1.5 }}>
                    <Paper 
                      elevation={0} 
                      sx={{ 
                        p: 1, 
                        border: '1px dashed', 
                        borderColor: 'primary.light',
                        borderRadius: 1,
                        backgroundColor: 'primary.50',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          borderColor: 'primary.main',
                          backgroundColor: 'primary.100',
                        }
                      }}
                    >
                      <RHFFileField name={`file${imageNumber}`} />
                    </Paper>
                  </AccordionDetails>
                </Accordion>
              </Grid>
            ))}

            <Grid item xs={12} sx={{ mt: 1 }}>
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <LoadingButton
                  loading={isSubmitting}
                  variant="contained"
                  size="medium"
                  startIcon={<UploadIcon sx={{ fontSize: '1.1rem' }} />}
                  type="submit"
                  sx={{
                    px: 3,
                    py: 1,
                    borderRadius: 1,
                    textTransform: 'none',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    boxShadow: 1,
                    '&:hover': {
                      boxShadow: 2,
                    }
                  }}
                >
                  {isSubmitting ? 'Uploading...' : 'Upload Images'}
                </LoadingButton>
              </Box>
            </Grid>
          </Grid>
        </FormProvider>
      </CardContent>
    </Card>
  );
}

export default UploadImage;
